/**
 * Customer FCM helpers — mirror the Driver App's atomic token-creation flow.
 *
 * Contract:
 * - Verify OTP is the single authoritative point at which a `push_tokens` row
 *   is created for the new device. `verifyOtp` calls `mintFreshFcmToken()`
 *   BEFORE invoking `customer-login.verify_otp` and passes `fcm_token` +
 *   `platform` in the body. The edge function persists it atomically with
 *   the new `active_session_id`.
 * - `useFcmRegistration` is a pure diff-gated safety net for OS-driven
 *   token rotations OUTSIDE a login. It uses `customer_fcm_last_registered`
 *   to skip duplicate writes when the OS reports the same token Verify OTP
 *   already registered, and requires an active `customer_session_id`.
 */
import { Capacitor } from '@capacitor/core';
import { invokeEdgeFunction } from '@/lib/edgeFunctions';
import { supabase } from '@/integrations/supabase/external-client';
import {
  getCustomerSessionId,
  handleEdgeResult,
} from '@/lib/customerSession';

const LAST_REGISTERED_KEY = 'customer_fcm_last_registered';

type LastRegistered = { customerId: string; token: string };

let cachedFcmToken: string | null = null;

export function getLastRegisteredFcm(): LastRegistered | null {
  try {
    const raw = localStorage.getItem(LAST_REGISTERED_KEY);
    return raw ? (JSON.parse(raw) as LastRegistered) : null;
  } catch {
    return null;
  }
}

export function setLastRegisteredFcm(value: LastRegistered | null) {
  try {
    if (value) localStorage.setItem(LAST_REGISTERED_KEY, JSON.stringify(value));
    else localStorage.removeItem(LAST_REGISTERED_KEY);
  } catch { /* ignore */ }
}

export function isAlreadyRegistered(customerId: string, token: string): boolean {
  const last = getLastRegisteredFcm();
  return !!last && last.customerId === customerId && last.token === token;
}

export function setCachedFcmToken(token: string | null) {
  cachedFcmToken = token;
}

export function getCachedFcmToken(): string | null {
  return cachedFcmToken;
}

/**
 * Mint a brand-new FCM token synchronously for the caller (used by
 * `verifyOtp` BEFORE invoking `customer-login`, so the new token is
 * created server-side at Verify OTP as the single authoritative point).
 *
 * Returns null on non-Android / non-native / failure.
 * Does NOT call `PushNotifications.register()` — that would emit a
 * 'registration' event with a token that may differ from the one
 * `FCM.refreshToken()` returns, creating a duplicate write outside the
 * OTP path.
 */
export async function mintFreshFcmToken(
  timeoutMs = 12000,
): Promise<{ token: string; platform: string } | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform !== 'android') return null;
  try {
    const { FCM } = await import('@capacitor-community/fcm');
    // Only delete the existing Firebase Installation if we've ever
    // registered before on this device. On a brand-new install the
    // delete+refresh round-trip frequently arrives after the timeout
    // window, leaving the caller with a null token and breaking the
    // Verify OTP write path. When no prior marker exists, just read
    // the current token directly — Android will mint one on first use.
    const hasPrior = !!getLastRegisteredFcm();
    const refreshPromise = (async () => {
      // Retry up to 3 attempts with backoff on transient FCM failures
      // (Play Services warm-up, transient network, etc).
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (hasPrior && attempt === 0) {
            try { await FCM.deleteInstance(); } catch { /* non-fatal */ }
          }
          const { token } = await FCM.refreshToken();
          if (token) return token;
        } catch (err) {
          lastErr = err;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (lastErr) console.warn('[customer-fcm] refreshToken retries exhausted', lastErr);
      return null;
    })();
    const token = await Promise.race<string | null>([
      refreshPromise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    if (!token) return null;
    cachedFcmToken = token;
    return { token, platform };
  } catch (err) {
    console.warn('[customer-fcm] mintFreshFcmToken failed', err);
    return null;
  }
}

/**
 * Post a new FCM token to the backend via dispatch-trip:register_customer_fcm_token.
 * Used ONLY by the diff-gated safety net inside useFcmRegistration for
 * true OS-driven rotations OUTSIDE a login.
 */
export async function postRegisterFcmToken(customerId: string, token: string) {
  const platform = Capacitor.getPlatform();
  try {
    // Get current access token to authenticate the edge call.
    let authToken: string | undefined;
    try {
      const { data } = await supabase.auth.getSession();
      authToken = data.session?.access_token;
    } catch { /* ignore */ }
    const res = await invokeEdgeFunction('dispatch-trip', {
      action: 'register_customer_fcm_token',
      customer_id: customerId,
      fcm_token: token,
      platform,
      session_id: await getCustomerSessionId(),
    }, authToken);
    handleEdgeResult(res);
    if (res?.data?.success === false) {
      console.warn('[customer-fcm] register_customer_fcm_token rejected', res?.data?.error);
    } else {
      setLastRegisteredFcm({ customerId, token });
      console.log('[customer-fcm] register_customer_fcm_token ok');
    }
  } catch (err) {
    console.warn('[customer-fcm] register_customer_fcm_token failed', err);
  }
}
